<script setup lang="ts">
/**
 * 代理商的授權（`/reseller/licenses`）——批93-C §2.2、批108 擴充。
 *
 * 代理商在這裡能做的事（批108 從兩件變成四件）：**看狀態**、**取證交給客戶**
 * （下載檔案／複製憑證）、**取加購子憑證**、**換發新證**。
 * 撤銷只有我方能做；要改成非標準內容一樣走〈授權申請〉的草稿流程
 * （換發的政策閘會把偏離模板的內容擋回去）。
 *
 * ★ 刻意**看不到主機細節**（hostname、host_info、代管主機名）：那是我方維運的資訊，
 *   後端 `/reseller/licenses` 的投影裡根本沒有這些欄位。這頁看得到的是「客戶站台好不好」
 *   ——最後心跳、版本、配額用量、到期倒數。
 */
import { computed, onMounted, ref, watch } from 'vue'

import FilterClearButton from '@/components/FilterClearButton.vue'
import LicenseDetailModal from '@/components/LicenseDetailModal.vue'
import QuotaSummary from '@/components/QuotaSummary.vue'
import ReissueModal from '@/components/ReissueModal.vue'
import ResellerRebindModal from '@/components/ResellerRebindModal.vue'
import UiButton from '@/components/UiButton.vue'
import { ApiError } from '@/api/client'
import type { ProductSpec } from '@/api/ls'
import * as reseller from '@/api/reseller'
import type { ResellerAddon, ResellerCustomer, ResellerLicense } from '@/api/reseller'
import { ADDON_FILENAME, LICENSE_FILENAME, copyText, saveTextFile } from '@/lib/download'
import { usePersistedFilters } from '@/lib/persistedFilters'
import { buildQuotaRows, planLabel, usageFrom } from '@/lib/quotas'
import { fmtSeen, staleLevel } from '@/lib/staleness'
import { formatRemaining, formatTerm } from '@/lib/terms'
import { customerOptionLabel } from '@/lib/customerLabel'
import { useDialogsStore } from '@/stores/dialogs'
import { vCardTable } from '@/lib/cardTable'

const dialogs = useDialogsStore()

const rows = ref<ResellerLicense[]>([])
const customers = ref<ResellerCustomer[]>([])
const products = ref<ProductSpec[]>([])
const loading = ref(true)
const loadError = ref('')
/**
 * keyset 分頁的續頁游標（null＝已經載完）。後端一頁 50 筆。
 *
 * ★ 批121：「只看生效中」改成**伺服器端**過濾（`bucket=active`）——以前是前端篩
 *   `status === 'active'`，那有兩個毛病：①「載入更多」載的是下一頁原始資料，勾著
 *   只看生效中時常常一列都沒增加 ②`status='active'` 會把**已過期但還沒被取代**的證
 *   也算成使用中，與我方後台的定義不一致（那邊的「生效中」要求未過期）。
 *   客戶下拉仍留前端（一家代理商的客戶量級是幾十家，而且下拉本來就只在已載入的列上篩）。
 */
const nextCursor = ref<string | null>(null)
const loadingMore = ref(false)
/** 四個桶的全表筆數（批121）；null＝還沒拿到／拿不到。 */
const counts = ref<reseller.ResellerLicenseCounts | null>(null)

async function loadPage(cursor: string | null) {
  const page = await reseller.listLicenses({
    ...(cursor ? { cursor } : {}),
    ...(onlyActive.value ? { bucket: 'active' } : {}),
  })
  rows.value = cursor ? [...rows.value, ...page.items] : page.items
  nextCursor.value = page.next_cursor
}

/** 計數失敗不擋畫面（只是分母）。 */
async function loadCounts() {
  try {
    counts.value = await reseller.licenseCounts()
  } catch {
    /* 保留上一次的數字 */
  }
}

onMounted(async () => {
  try {
    const [, , custs, prods] = await Promise.all([
      loadPage(null),
      loadCounts(),
      // 客戶下拉的來源；批93-D 起是分頁端點，取一頁上限即可（代理商的客戶量級是幾十家）。
      reseller.listCustomers({ limit: 200 }),
      reseller.listProducts(),
    ])
    customers.value = custs.items
    products.value = prods
    loadError.value = ''
  } catch (e) {
    loadError.value = errText(e, '讀取授權清單失敗')
  } finally {
    loading.value = false
  }
})

async function loadMore() {
  if (!nextCursor.value || loadingMore.value) return
  loadingMore.value = true
  try {
    await loadPage(nextCursor.value)
  } catch (e) {
    loadError.value = errText(e, '讀取下一頁失敗')
  } finally {
    loadingMore.value = false
  }
}

function errText(e: unknown, fallback: string): string {
  const d = e instanceof ApiError && e.payload ? (e.payload as { detail?: unknown }).detail : undefined
  if (d == null) return fallback
  if (typeof d === 'string') return d
  const obj = d as { message?: string; code?: string }
  return obj.message ?? obj.code ?? JSON.stringify(d)
}

// --- 過濾（客戶下拉＋狀態桶都留前端：一家代理商的授權量級是幾十張）---
// 批186：兩個條件記住；「只看生效中」預設開，清除會回到勾選。
const pf = usePersistedFilters('reseller-licenses', { custFilter: '', onlyActive: true })
const { custFilter, onlyActive } = pf.refs
const { dirty: filtersDirty } = pf
/** 下面的 `watch(onlyActive)` 會重載；客戶下拉只在前端過濾，改值即可。 */
const clearFilters = () => pf.clear()
// ★ 批121：狀態已經由伺服器篩掉了（`bucket=active`），這裡只剩客戶下拉。
//   再篩一次 `status === 'active'` 會把伺服器認定「生效中」的列又吃掉一部分。
const visible = computed(() =>
  rows.value.filter((l) => !custFilter.value || l.customer_id === custFilter.value),
)
// ★ 批121：「只看生效中」是伺服器端過濾 ⇒ 勾選要重載第一頁。
//   放在 `onlyActive` 宣告之後（提前 watch 會是 TDZ 錯誤）。
watch(onlyActive, () => void loadPage(null))

function customerName(l: ResellerLicense): string {
  return l.customer_name ?? customers.value.find((c) => c.id === l.customer_id)?.name ?? '—'
}
function specOf(key: string | undefined): ProductSpec | null {
  return products.value.find((p) => p.key === key) ?? null
}
function productName(key: string | undefined): string {
  return products.value.find((p) => p.key === key)?.name ?? (key ?? '—')
}
const STATUS_LABELS: Record<string, string> = {
  active: '使用中',
  superseded: '已換發',
  revoked: '已撤銷',
}
function statusLabel(s: string): string {
  return STATUS_LABELS[s] ?? s
}
function fmtDate(iso: string | null | undefined): string {
  return iso ? iso.slice(0, 10) : '—'
}
/** 到期倒數：後端沒帶就自己從 `expires_at` 算（兩邊都沒有＝尚未啟用）。 */
function daysLeft(l: ResellerLicense): number | null {
  if (l.days_to_expiry != null) return Math.floor(l.days_to_expiry)
  if (!l.expires_at) return null
  return Math.ceil((Date.parse(l.expires_at) - Date.now()) / 86_400_000)
}
function expiryText(l: ResellerLicense): string {
  // ★ 批108：期限可以短到分鐘 → 未啟用印「30 分鐘」而不是「0 天」，剩餘時間自動選粒度
  //   （與我方後台、客戶 portal、CS 站台同一套措辭，見 lib/terms.ts）。
  if (!l.expires_at) return `未啟用（${formatTerm(l.term_days, l.term_minutes)}）`
  const remaining = formatRemaining(l.expires_at)
  return remaining ? `${fmtDate(l.expires_at)}（${remaining}）` : fmtDate(l.expires_at)
}
/** 30 天內到期＝橘字（該提醒客戶續約了）。 */
function expiryWarn(l: ResellerLicense): boolean {
  const d = daysLeft(l)
  return d != null && d <= 30
}

/**
 * 配額列（用量來自心跳；沒回報過只印上限）。
 *
 * ★★ 批114 修 bug：原本這裡是**手工組**一個 usage 物件，只抄了其中一個 `*_in_use` 欄，
 *   於是最主要那一項的用量永遠是 null——代理商既看不到用量，也永遠不會出現「額度已滿」。
 *   改走 `usageFrom(l)`：代理商的投影是**扁平**的 `*_in_use` 欄，那支會把所有
 *   「鍵名以 `_in_use` 結尾且值是數字」的欄一起帶進來，不會再漏。
 *   （這頁刻意不再判 `last_seen`：從未回報的列本來就沒有那些欄，`usedOf` 自然回 null。）
 */
function quotaRows(l: ResellerLicense) {
  return buildQuotaRows(specOf(l.product), l.quotas, usageFrom(l))
}

// --- 取證交付（批108）-------------------------------------------------------
const busyId = ref<string | null>(null)

/** 這張證能不能人工帶走（代管站由中台派送，後端一律 403）。 */
function canTakeOut(l: ResellerLicense): boolean {
  return !l.hosted && l.status === 'active'
}
function takeoutHint(l: ResellerLicense): string | undefined {
  if (l.hosted) return '這台由代管方直接派送授權，不提供人工取證'
  if (l.status !== 'active') return '只有生效中的授權可以下載（避免客戶誤裝失效證）'
  return undefined
}

/**
 * 取回授權原文並存成檔案交給客戶。
 *
 * 直接下檔而不是只複製到剪貼簿：代理商多半是把檔案 email 給客戶，剪貼簿在這條動線上
 * 會多一次「貼到哪裡存成什麼檔名」的自由發揮。**後端會記稽核**（誰把哪張證帶走）。
 *
 * ★ 批108 修正檔名：舊的是 `license-<8碼>.txt`，與安裝手冊的 `license/license.ipolic`
 *   對不上——客戶拿到還要先改名才能用。
 */
async function download(l: ResellerLicense) {
  busyId.value = l.license_id
  try {
    const res = await reseller.licenseFile(l.license_id)
    saveTextFile(res.blob, LICENSE_FILENAME)
    await dialogs.notify({
      title: '已下載授權檔',
      message: `把 ${LICENSE_FILENAME} 交給客戶，放到站台的 license/ 目錄後重啟即可生效（或請他貼進站台〈系統 → 授權 → 手動安裝 .lic〉）。`,
    })
  } catch (e) {
    await dialogs.notify({ title: '下載失敗', message: errText(e, '下載授權檔失敗') })
  } finally {
    busyId.value = null
  }
}

/** 複製憑證原文（客戶在線上、直接請他貼進站台時比較快）。 */
async function copyLicense(l: ResellerLicense) {
  busyId.value = l.license_id
  try {
    const res = await reseller.licenseFile(l.license_id)
    if (await copyText(res.blob)) {
      await dialogs.notify({
        title: '已複製憑證',
        message: '請客戶貼進站台〈系統 → 授權 → 手動安裝 .lic〉即可。',
      })
    } else {
      await dialogs.notify({
        title: '無法自動複製',
        message: `這個瀏覽器不允許存取剪貼簿，請手動複製下面這段：\n\n${res.blob}`,
      })
    }
  } catch (e) {
    await dialogs.notify({ title: '取得憑證失敗', message: errText(e, '取得憑證失敗') })
  } finally {
    busyId.value = null
  }
}

// --- 加購子憑證（批108）-----------------------------------------------------
//
// 代理商 realm 原本**完全看不到加購**，離線客戶的加購子證只能由我方經手。
// 清單直接掛在授權列上（後端一次 IN 批載），要原文才打端點。
/**
 * 批110：詳情 modal 的目標（null＝關閉）。取代原本的子證展開列——代理商的子證資料本來
 * 就在清單投影裡（`l.addons`），所以這裡不必像我方後台那樣再打一次端點。
 */
const detailFor = ref<ResellerLicense | null>(null)

/** 這張證還沒到生效時間嗎（LS #5496）。站台在那之前繼續用手上那張，不會轉唯讀。 */
function notYet(l: ResellerLicense): boolean {
  return !!l.not_before && new Date(l.not_before).getTime() > Date.now()
}

async function saveAddon(a: ResellerAddon) {
  try {
    const res = await reseller.addonFile(a.addon_id)
    saveTextFile(res.blob, ADDON_FILENAME)
    await dialogs.notify({
      title: '已下載子憑證',
      message: `「${a.label}」已存成 ${ADDON_FILENAME}，請客戶貼進站台〈系統 → 授權 → 手動安裝 .lic〉。`,
    })
  } catch (e) {
    await dialogs.notify({ title: '下載失敗', message: errText(e, '下載子憑證失敗') })
  }
}

async function copyAddon(a: ResellerAddon) {
  try {
    const res = await reseller.addonFile(a.addon_id)
    if (!(await copyText(res.blob))) {
      await dialogs.notify({
        title: '無法自動複製',
        message: `這個瀏覽器不允許存取剪貼簿，請手動複製下面這段：\n\n${res.blob}`,
      })
      return
    }
    await dialogs.notify({ title: '已複製子憑證', message: `「${a.label}」的簽章原文已複製到剪貼簿。` })
  } catch (e) {
    await dialogs.notify({ title: '取得子憑證失敗', message: errText(e, '取得子憑證失敗') })
  }
}

// --- 換機申請（批108）-------------------------------------------------------
//
// 客戶換硬體時代理商原本完全幫不上忙（換機只有客戶自己在 portal 送得出來）。
// 這裡只負責**送件**，核准仍然是我方的事。
const rebindFor = ref<ResellerLicense | null>(null)

async function onRebindSubmitted() {
  await dialogs.notify({
    title: '已送出換機申請',
    message: '愛+1 會盡快處理。核准後新證會出現在這一頁，用「下載授權檔」交給客戶即可。',
  })
}

// --- 換發新證（批108）-------------------------------------------------------
//
// 走與我方後台逐位元相同的本體；內容偏離套裝模板會被政策閘擋回申請流程。
const reissueFor = ref<ResellerLicense | null>(null)

async function onReissued() {
  await loadPage(null)
  await dialogs.notify({
    title: '已換發新證',
    message: '舊的那張已標成「已被取代」。請用同一列的「下載授權檔」把新證交給客戶。',
  })
}
</script>

<template>
  <div class="page">
    <div class="page-head">
      <h1>授權</h1>
      <span class="count">{{ visible.length }}</span>
      <span class="spacer" />
      <label class="hf">
        <span>客戶</span>
        <select v-model="custFilter">
          <option value="">全部客戶</option>
          <option v-for="c in customers" :key="c.id" :value="c.id">{{ customerOptionLabel(c, { ownership: false }) }}</option>
        </select>
      </label>
      <label class="opt"><input v-model="onlyActive" type="checkbox" /> 只看生效中</label>
      <FilterClearButton :dirty="filtersDirty" @click="clearFilters" />
    </div>

    <p v-if="loadError" class="err">{{ loadError }}</p>
    <div v-if="loading" class="loading-wrap"><span class="spinner lg" /></div>

    <div v-else class="card">
      <div v-if="!visible.length" class="empty">
        <div class="empty-sub">
          目前沒有授權。到〈授權申請〉建立草稿並確認送出後，核發的授權會出現在這裡。
        </div>
      </div>
      <div v-else class="tbl-scroll" data-hscroll>
        <table v-card-table class="tbl card-table">
          <thead>
            <!-- 6 欄（批110）：與我方後台同一套版型，理由見 `LicensesView.vue` 的註解。 -->
            <tr>
              <th>客戶</th><th>產品・站台</th><th>狀態・到期</th><th>配額</th>
              <th>授權內容</th><th></th>
            </tr>
          </thead>
          <tbody>
            <!-- 批108：一列授權可能多一列「子證」展開，所以外層改成 template v-for -->
            <template v-for="l in visible" :key="l.license_id">
            <tr>
              <td>
                <strong>{{ customerName(l) }}</strong>
                <div class="subtle sub-line">
                  <code class="mono">{{ l.license_id.slice(0, 8) }}</code>
                </div>
              </td>
              <td class="nowrap">
                <div class="prod-name">{{ productName(l.product) }}</div>
                <div class="subtle">{{ l.instance_name || '—' }}</div>
                <div class="seen sub-line" :class="staleLevel(l.last_seen)">
                  {{ fmtSeen(l.last_seen) }}
                  <span v-if="l.app_version" class="ver">v{{ l.app_version }}</span>
                </div>
              </td>
              <td class="status">
                <span class="status-pill" :class="l.status">{{ statusLabel(l.status) }}</span>
                <!-- 批110：延後生效（LS #5496）。站台在那之前繼續用手上那張證。 -->
                <span v-if="notYet(l)" class="chip pending">尚未生效</span>
                <div class="exp-line" :class="{ warn: expiryWarn(l) }">{{ expiryText(l) }}</div>
              </td>
              <!-- 批114：原本硬編單一數字欄。改成資料驅動的配額摘要（同我方後台）。 -->
              <td class="quota">
                <QuotaSummary :spec="specOf(l.product)" :quotas="l.quotas" :usage="l" />
              </td>
              <!-- 批110：套裝／配額／子證收成一句摘要，點開看全部（同我方後台）。 -->
              <td class="content">
                <button type="button" class="content-btn" @click="detailFor = l">
                  <span class="plan">
                    {{ l.plan ? planLabel(specOf(l.product), l.plan) : '客製' }}
                  </span>
                  <span class="sum">
                    額度 {{ quotaRows(l).length }}<template
                      v-if="l.addons?.length"
                    >・子證 {{ l.addons.length }}</template>
                  </span>
                  <span v-if="quotaRows(l).some((q) => q.over)" class="chip over">額度已滿</span>
                </button>
              </td>
              <td class="acts-cell">
                <div class="acts">
                  <!-- 批108：代管站**反灰但不藏**（藏起來會被當成 bug） -->
                  <UiButton
                    variant="primary"
                    size="sm"
                    :loading="busyId === l.license_id"
                    :disabled="!canTakeOut(l)"
                    :title="takeoutHint(l) ?? '取回授權原文並存成檔案（我方會記錄這次下載）'"
                    @click="download(l)"
                  >下載授權檔</UiButton>
                  <!-- ★ 次要動作一律 `secondary`（有框）——與我方後台的授權列同一套。
                       留空會 fallback 成 `ghost`，一整排並列時看不出哪些可按。 -->
                  <UiButton
                    variant="secondary"
                    size="sm"
                    :disabled="!canTakeOut(l)"
                    :title="takeoutHint(l)"
                    @click="copyLicense(l)"
                  >複製憑證</UiButton>
                  <!-- 批113：`host_instance_id` 為空＝這張證沒綁機器，換發（以機器為錨點
                       重簽）一定是 422 `base_has_no_host`——按得下去只是讓人白按一次。 -->
                  <UiButton
                    variant="secondary"
                    size="sm"
                    :disabled="l.hosted || l.status !== 'active' || !l.host_instance_id"
                    :title="
                      l.hosted
                        ? '代管站的授權由中台派送，不走換發'
                        : !l.host_instance_id
                          ? '未綁主機的授權無法換發，請改到〈授權申請〉提出'
                          : undefined
                    "
                    @click="reissueFor = l"
                  >換發新證</UiButton>
                  <UiButton
                    variant="secondary"
                    size="sm"
                    :disabled="l.hosted || l.status !== 'active'"
                    :title="l.hosted ? '代管站換主機由中台處理' : '客戶換了機器時用這個（核准由愛+1 處理）'"
                    @click="rebindFor = l"
                  >申請換機</UiButton>
                </div>
              </td>
            </tr>
            <!-- 批108：加購子憑證（展開列）。資料直接來自清單投影，不另外打端點。 -->
            </template>
          </tbody>
        </table>
      </div>
      <!-- keyset 分頁：後端一頁 50 筆。`next_cursor` 非 null 才畫這一列。 -->
      <div v-if="nextCursor" class="more">
        <UiButton size="sm" :loading="loadingMore" @click="loadMore">載入更多</UiButton>
        <!-- 批121：分母講清楚是「符合目前條件的總數」，不然「已載入 50」與畫面上的幾列並排時沒人看得懂。 -->
        <span class="subtle">
          已顯示 {{ rows.length }}<template v-if="counts">
            / {{ onlyActive ? counts.active : counts.all }}</template> 張
        </span>
      </div>
    </div>

    <!-- 批108：替客戶送換機申請（核准仍在我方） -->
    <ResellerRebindModal
      :open="rebindFor !== null"
      :license="rebindFor"
      @close="rebindFor = null"
      @done="onRebindSubmitted"
    />

    <!-- 批108：換發新證（與我方後台共用同一支元件與後端本體） -->
    <LicenseDetailModal
      :open="!!detailFor"
      :license="(detailFor as never)"
      :spec="detailFor ? (specOf(detailFor.product) ?? null) : null"
      :addons="(detailFor?.addons ?? []) as never"
      :can-reissue="!!detailFor && !detailFor.hosted && detailFor.status === 'active'"
      @addon-save="(a) => saveAddon(a as never)"
      @addon-copy="(a) => copyAddon(a as never)"
      @close="detailFor = null"
      @reissue="
        () => {
          reissueFor = detailFor
          detailFor = null
        }
      "
    />

    <ReissueModal
      :open="reissueFor !== null"
      :license="reissueFor"
      :spec="reissueFor ? (specOf(reissueFor.product) ?? null) : null"
      is-reseller
      @close="reissueFor = null"
      @done="onReissued"
    />
  </div>
</template>

<style scoped>
.page-head {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  margin-bottom: var(--sp-4);
}
.page-head h1 {
  margin: 0;
  font-size: var(--fs-20);
  color: var(--fg);
}
.count {
  font-size: var(--fs-12);
  color: var(--fg-3);
  background: var(--bg);
  padding: 1px 8px;
  border-radius: 999px;
}
.spacer {
  flex: 1;
}
.hf,
.opt {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: var(--fs-12);
  color: var(--fg-3);
  white-space: nowrap;
}
.err {
  color: var(--danger);
  font-size: var(--fs-13);
}
.nowrap {
  white-space: nowrap;
}
.sub-line {
  margin-top: 2px;
  font-size: var(--fs-11);
  line-height: var(--lh-base);
}
.ver {
  margin-left: 6px;
  color: var(--fg-4);
}
/* 批114：配額摘要欄。`<QuotaSummary>` 自己 nowrap＋省略號，這裡只壓欄寬。 */
.quota {
  max-width: 180px;
}
.quota {
  max-width: 320px;
}
.quota .chip {
  margin: 1px 3px 1px 0;
  font-size: var(--fs-11);
}
.exp {
  white-space: nowrap;
  font-size: var(--fs-12);
}
.exp.warn {
  color: var(--warning);
  font-weight: 600;
}
.more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--sp-2);
  padding: var(--sp-3);
  border-top: 1px solid var(--border);
}
</style>
